import type {
  ConversationDataResponse,
  DeleteConversationResponse,
  FeedbackResponse,
  FilePayload,
  FileUploadResponse,
  MessageHistoryResponse,
  RenameConversationResponse,
  StopGeneratingResponse,
} from '@typings/agent'
import type { Agent, Conversation, FileRecord, Message } from '@typings/dify'
import axios, { AxiosInstance } from 'axios'

import { environment } from '@envs/environment'
import { t } from 'i18next'
import { getApiHeaders } from './api.service'

const { apimUrl, apimSubscriptionKey, apimVersion } = environment;

/**
 * Creates an Axios client for a specific user
 * @returns {AxiosInstance}
 */
const createApiClient = async (): Promise<AxiosInstance> => {
  return axios.create({
    baseURL: apimUrl+"/forward",
    headers: await getApiHeaders(),
  });
}

const difyService = {
  /**
   * Send a chat message with a specific agent
   * @param {string} agent - Agent identifier (e.g., 'agent1', 'agent2')
   * @param {string} query - User input/question
   * @param {FilePayload[]} files - Additional variables for the app
   * @param {string} responseMode - Response mode (streaming or blocking)
   * @param {string} conversationId - ID for session persistence
   * @param {string} user - Unique user identifier
   * @returns {Promise}
   */
  sendMessage: async (
    agent: Agent,
    query: string,
    conversationId: string = '',
    responseMode: string = 'streaming',
    files: FilePayload[] = [],
    onStreamData?: (data: Message) => void,
    onStreamError?: (error: Error) => void,
    onTaskId?: (taskId: string) => void,
  ): Promise<Message> => {
    const agentId = agent.id;

    if (!agentId) {
      throw new Error(`Invalid agent: ${agent}`)
    }

    const baseURL = apimUrl;

    const payload = {
      query: query,
      inputs: {},
      files: files,
      response_mode: responseMode,
      conversation_id: conversationId,
      agent_id: agentId,
    };

    try {
      let conversation: ConversationDataResponse | null = null;

      if (responseMode === 'streaming' && onStreamData) {
        // Start streaming the chat messages
        const response = await fetch(`${baseURL}/forward/chat-messages`, {
          method: 'POST',
          headers: await getApiHeaders(),
          body: JSON.stringify(payload),
        });

        if (!response.body) {
          throw new Error('Streaming response body is null.')
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');

        let messageContent = '';
        let messageId = '';
        let conversation = null;
        let conversationParsed = false;
        let files: FileRecord[] = [];

        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          const chunkString = decoder.decode(value, { stream: true });
          // Split the data into lines and parse each `data:` line
          const lines = chunkString.split('\n').filter((line) => line.startsWith('data:'));

          for (const line of lines) {
            const cleanedLine = line.replace(/^data:\s*/, ''); // Remove the `data:` prefix
          
            try {
              const parsedChunk = JSON.parse(cleanedLine);

              if (parsedChunk.message_id) {
                messageId = parsedChunk.message_id;
              }

              if (parsedChunk.task_id && onTaskId) {
                onTaskId(parsedChunk.task_id);
              }

              if (parsedChunk.conversation_id && !conversationParsed) {
                // Fetch conversation data once
                const resConv = await fetch(`${baseURL}/forward/conversations?agent_id=${agentId}&limit=1`, {
                  headers: await getApiHeaders()
                });

                const convData = await resConv.json();
                
                conversation = convData.data.find((c: ConversationDataResponse) => c.id === parsedChunk.conversation_id);

                if (conversation && conversation.name !== 'New conversation') {
                  conversationParsed = true;
                }
              }

              if (
                parsedChunk?.data?.inputs &&
                parsedChunk?.data?.inputs['sys.files'] &&
                parsedChunk?.data?.inputs['sys.files']?.length > 0
              ) {
                files = [];

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                parsedChunk.data.inputs['sys.files'].forEach((file: any) => {
                  files.push({
                    id: file.id,
                    filename: file.filename,
                    size: file.size,
                    extension: file.extension,
                    mime_type: file.mime_type,
                    url: file.url,
                  })
                });

                await fetch(`${baseURL}/forward/conversations-name`, {
                  method: 'POST',
                  headers: await getApiHeaders(),
                  body: JSON.stringify({
                    name: files[0].filename,
                    conversation_id: conversation.id,
                    agent_id: agentId,
                  }),
                });
              }

              if (parsedChunk.answer) {
                messageContent += parsedChunk.answer;

                onStreamData({
                  id: messageId,
                  content: messageContent,
                  role: 'assistant',
                  conversationId: conversation.id,
                  conversationTitle:
                    files && files.length > 0 && files[0].filename ? files[0].filename : conversation.name,
                  timestamp: new Date().toISOString(),
                  files: files,
                  liked: false,
                  isNew: true,
                  inputs: {}
                });
              }

              if (parsedChunk.event === 'error') {
                onStreamError?.(parsedChunk.message);
              }

              if (parsedChunk.task_id && onTaskId) {
                onTaskId(parsedChunk.task_id);
              }
            } catch (parseError) {
              console.error('Error parsing chunk:', parseError, cleanedLine)
            }
          }
        }

        let conversationTitle = files && files.length > 0 && files[0].filename ? files[0].filename : (conversation?.name || t('chat.new_conversation'));

        return {
          id: messageId,
          content: messageContent,
          role: 'assistant',
          conversationId: conversation?.id,
          conversationTitle: conversationTitle,
          timestamp: new Date().toISOString(),
          files: files,
          liked: false,
          isNew: true,
          inputs: {}
        };
      } else {
        // Blocking mode (non-streaming)
        const res = await fetch(`${baseURL}/forward/chat-messages`, {
          method: 'POST',
          headers: await getApiHeaders(),
          body: JSON.stringify(payload),
        });

        const resData = await res.json();

        const resConv = await fetch(`${baseURL}/forward/conversations?agent_id=${agentId}&limit=1`, {
          headers: await getApiHeaders()
        });

        const convData = await resConv.json();

        conversation = convData.data.find((c: ConversationDataResponse) => c.id === resData.conversation_id);

        return {
          id: resData.message_id,
          content: resData.answer,
          role: 'assistant',
          conversationId: resData.conversation_id,
          conversationTitle: conversation?.name,
          timestamp: resData.created_at,
          liked: false,
          isNew: true,
          inputs: {}
        };
      }
    } catch (error) {
      console.error("Erreur lors de l'envoi du message:", error);

      throw error;
    }
  },

  /**
   * Upload a file for the selected agent
   * @param {string} agent - Agent identifier
   * @param {File} file - The file to upload
   * @returns {Promise}
   */
  uploadFile: async (agent: Agent, file: File): Promise<FileUploadResponse> => {
    const agentId = agent.id;

    if (!agentId) {
      throw new Error(`Invalid agent: ${agent}`);
    }

    const apiClient = await createApiClient();

    const formData = new FormData();

    formData.append('file', file);
    formData.append('agent_id', agentId.toString());

    const { data } = await apiClient.post('/files-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Ocp-Apim-Subscription-Key': apimSubscriptionKey,
        'Api-Version': apimVersion,
        'Access-Control-Allow-Origin': '*'
      },
    })

    return data;
  },

  /**
   * Fetch conversation history
   * @param {string} agent - Agent identifier
   * @param {string} conversationId - ID of the conversation
   * @param {number} limit - Number of messages to fetch
   * @returns {Promise}
   */
  getConversationHistory: async (
    agent: Agent,
    conversationId: string,
    limit: number = 100,
  ): Promise<Message[]> => {
    const agentId = agent.id;

    if (!agentId) {
      throw new Error(`Invalid agent: ${agent}`)
    }

    const apiClient = await createApiClient();

    const { data: res } = await apiClient.get('/messages', {
      params: { conversation_id: conversationId, agent_id : agentId, limit },
    })

    const messages = res.data.map((message: MessageHistoryResponse) => {
      const files: FileRecord[] = []

      if (message.message_files && message.message_files.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        message.message_files.forEach((file: any) => {
          files.push({
            id: file.id,
            filename: file.filename,
            size: file.size,
            extension: file.extension,
            mime_type: file.mime_type,
            url: file.url,
          })
        })
      }
      return [
        {
          id: 'query_' + message.id,
          content: message.query,
          role: 'user' as const,
          liked: false,
          timestamp: message.created_at,
          inputs: message.inputs|| {} //Récupérer les documents envoyés par l'utilisateur
        },
        {
          id: message.id,
          content: message.answer,
          role: 'assistant' as const,
          liked: message.feedback === null || message.feedback.rating === null ? false : true,
          timestamp: message.created_at,
          files: files,
        },
      ]
    })

    return messages.flat()
  },

  /**
   * Get the conversations of an agent
   * @param {string} agent - Agent identifier
   * @returns {Promise}
   */
  getConversations: async (agent: Agent, limit: number = 100): Promise<Conversation[]> => {
    const agentId = agent.id;

    if (!agentId) {
      throw new Error(`Invalid agent: ${agent}`)
    }

    const apiClient = await createApiClient();

    const { data: res } = await apiClient.get('/conversations', {
      params: { agent_id : agentId, limit },
    })

    if (res.data.length === 0) {
      return [];
    }

    return res.data.map((conversation: ConversationDataResponse) => {
    // Extraire les fichiers de la conversation
    const files: FileRecord[] = [];
    if (conversation.inputs?.Document && Array.isArray(conversation.inputs.Document)) {
      conversation.inputs.Document.forEach((file: any) => {
        files.push({
          id: file.related_id,
          filename: file.filename,
          size: file.size,
          extension: file.extension,
          mime_type: file.mime_type,
          url: file.remote_url,
        });
      });
    }
    
    return {
      id: conversation.id,
      title: conversation.name,
      timestamp: conversation.created_at,
      messages: [],
      isFavorite: false,
      files: files, // Ajouter les fichiers à la conversation
    }
  })
},

  /**
   * Provide feedback on a message
   * @param {string} agent - Agent identifier
   * @param {string} messageId - Message ID
   * @param {string} rating - Feedback rating ('like')
   * @returns {Promise}
   */
  sendFeedback: async (agent: Agent, messageId: string, rating: string | null): Promise<FeedbackResponse> => {
    const agentId = agent.id;

    if (!agentId) {
      throw new Error(`Invalid agent: ${agent}`);
    }

    const apiClient = await createApiClient();

    return apiClient.post(`/messages-feedbacks`, {
      rating,
      agent_id : agentId,
      message_id: messageId,
    })
  },

  /**
   * Delete a conversation
   * @param {string} agent - Agent identifier
   * @param {string} conversationId - Conversation ID
   * @returns {Promise}
   */
  deleteConversation: async (agent: Agent, conversationId: string): Promise<DeleteConversationResponse> => {
    const agentId = agent.id;

    if (!agentId) {
      throw new Error(`Invalid agent: ${agent}`);
    }

    const apiClient = await createApiClient();

    return apiClient.delete(`/conversations?agent_id=${agentId}&conversation_id=${conversationId}`)
  },

  /**
   * Rename a conversation
   * @param agent string
   * @param conversationId string
   * @param name string
   * @returns {Promise}
   */
  renameConversation: async (
    agent: Agent,
    conversationId: string,
    name: string,
  ): Promise<RenameConversationResponse> => {
    const agentId = agent.id;
    
    if (!agentId) {
      throw new Error(`Invalid agent: ${agent}`);
    }

    const apiClient = await createApiClient();

    return apiClient.post(`/conversations-name`, {
      name,
      conversation_id: conversationId,
      agent_id: agentId,
    })
  },

  /**
   * Stop generating
   * @param {string} agent - Agent identifier
   * @param {string} taskId - Conversation ID
   * @returns {Promise}
   */
  stopGenerating: async (agent: Agent, taskId: string): Promise<StopGeneratingResponse> => {
    const agentId = agent.id;

    if (!agentId) {
      throw new Error(`Invalid agent: ${agent}`)
    }

    const apiClient = await createApiClient();

    return apiClient.post(`/chat-messages-stop`, {
      agent_id: agentId,
      task_id: taskId
    })
  },
}

export default difyService
